import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronDown, Plus, Minus, FileText, Tag, Send, Workflow, Box, Calendar, GitBranch, PenTool, Layers, FolderTree } from "lucide-react";
import { CtaForm } from "../components/CtaForm";
import GranthaDocumentFabric from "../components/GranthaDocumentFabric";
import GranthaLogo, { GranthaIcon } from "../components/GranthaLogo";

function CountUp({ end, prefix = "", suffix = "", duration = 1600, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(end * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const d = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{prefix}{d}{suffix}</span>;
}

const MODULES = [
  { layer: 1, slug: "document-definition", name: "Document Definition", Icon: FileText, body: "Define document types, attributes, metadata schemas, numbering rules per project / client / discipline. Supports KKS, CFIHOS, or custom numbering conventions." },
  { layer: 1, slug: "tag-definition", name: "Tag Definition", Icon: Tag, body: "Define instrument, equipment, line, valve, and electrical tag taxonomies with attribute schemas (MTR, SAT, criticality, hazardous-area classification)." },
  { layer: 1, slug: "pbs", name: "PBS Tree", Icon: FolderTree, body: "Hierarchical asset decomposition: Refinery → Unit → System → Equipment → Component. Every document and tag anchors to a PBS node." },
  { layer: 2, slug: "document-register", name: "Document Register", Icon: Layers, body: "Canonical register — full revision history, metadata, discipline, status, Plan/Forecast/Actual dates, author, approver, sign-off chain. document servers inline editing. PDF/DWG/DGN viewer with Fabric.js markup." },
  { layer: 2, slug: "tag-management", name: "Tag Creation", Icon: Tag, body: "Create tags against defined taxonomy. Bulk import from Smart P&ID, 3D plant-design suites, plant-engineering platforms. Tag-to-document cross-referencing." },
  { layer: 2, slug: "tag-register", name: "Tag Register", Icon: Tag, body: "Master tag list — every instrument, line, equipment asset with full attribute data, linked documents, MTR/ITP/SAT certificates, vendor data." },
  { layer: 3, slug: "dci", name: "DCI (Document Control Index)", Icon: Calendar, body: "Plan / Forecast / Actual date logic for every deliverable. Visual S-curve dashboard per discipline. Aging analytics, late-document escalation, vendor-submission tracking." },
  { layer: 3, slug: "transmittal", name: "Transmittal Module", Icon: Send, body: "Formal inter-party document exchange. Auto-generates transmittal numbers, package contents, receipts, acknowledgements. Client ↔ EPC ↔ Vendor flows. Audit-trailed to the minute." },
  { layer: 3, slug: "markup-review", name: "Markup & Review", Icon: PenTool, body: "Collaborative review with Fabric.js overlay on PDF/DWG/DGN. Comment threads, resolution tracking, auto revision-clouds, IFC/IFR/IFA status flags." },
  { layer: 4, slug: "qto", name: "QTO Module", Icon: Box, body: "Quantity Takeoff from 3D models via 3D viewer engines. Extract BOM, pipe lengths, equipment counts, structural-steel tonnage. Push to legacy schedulers and ERP procurement modules." },
  { layer: 4, slug: "workflow-engine", name: "Workflow Execution Engine", Icon: Workflow, body: "The orchestration spine. N-level parallel / conditional / delegable routing. canUserExecuteStep() enforcement. SLA timers, auto-escalation, digital signatures (DSC + Aadhaar eSign)." },
  { layer: 4, slug: "workflow-engine", name: "GitOps Audit Replay", Icon: GitBranch, body: "Full workflow replay for audit — every click, signature, comment preserved with timestamp.", dedupe: true },
];

const LAYERS = [
  { n: 1, name: "Definition & Structure" },
  { n: 2, name: "Capture & Register" },
  { n: 3, name: "Lifecycle Control" },
  { n: 4, name: "Execution & Extraction" },
];

const FAQS = [
  { q: "How does Docuweave compare to Documentum, OpenText, or document libraries?", a: "Docuweave is purpose-built for engineering — native DWG/DGN/IFC viewing, tag taxonomy, revision-cloud automation, and PBS-anchored workflows. document libraries is a generic document library; Documentum requires 6–12 months and ₹3–8 Cr to configure for EPC workflows. Docuweave deploys in 8–10 weeks." },
  { q: "Can Docuweave be deployed on-premise for high-security clients?", a: "Yes. Docuweave supports Azure, AWS, and fully on-premise deployments. For Defense, Nuclear, and PSU clients we deploy to air-gapped environments with no external dependencies." },
  { q: "How does the document servers integration work?", a: "Docuweave deploys an document servers alongside the application. Users edit Word / Excel / PowerPoint files inline in the browser with full track-changes. Our reference deployment is production-validated." },
  { q: "What document numbering conventions does Docuweave support?", a: "KKS (German power-plant standard), CFIHOS (capital facilities information handover), client-specific schemes, and fully custom rule-based numbering. Rules are configurable per project, discipline, and document type." },
  { q: "Can we migrate from our existing EDMS or document libraries?", a: "Yes. Docuweave includes migration toolkits for document libraries, Documentum, OpenText, Meridian, and engineering-document tools. Typical migration: 6–10 weeks for <5M documents, preserving full revision history, metadata, and permissions." },
  { q: "How does Docuweave handle digital signatures for regulated industries?", a: "Docuweave supports DSC-based signatures (India IT Act 2000 compliant), Aadhaar eSign for Indian users, and Adobe Sign / e-signature platforms integration for international workflows. Signature audit trails are preserved indefinitely." },
  { q: "What's the typical deployment timeline?", a: "8–12 weeks for standard deployments. Phase 1 (weeks 1-4): config, taxonomy, user provisioning. Phase 2 (weeks 5-8): integrations, migration, workflow setup. Phase 3 (weeks 9-12): UAT, training, go-live." },
  { q: "Is Docuweave AI-powered?", a: "Yes — Docuweave ships with a built-in cognitive layer: auto-tagging from content, smart search via MongoDB Atlas Vector Search, duplicate-revision detection, markup-sentiment analysis, and integration with Cognara (Capexara's GenAI Agent) for natural-language document queries." },
];

export default function PlatformGrantha() {
  return (
    <>
      <Section1Hero />
      <Section2Problem />
      <Section3Architecture />
      <Section4Viewer />
      <Section5Workflow />
      <Section7Industries />
      <Section8Outcomes />
      <Section9Faq />
      <Section10Cta />
    </>
  );
}

/* ───── S1 Hero ─────────────────────────────────── */
function Section1Hero() {
  return (
    <section className="grantha-hero" data-testid="grantha-hero">
      <div className="container grantha-hero-grid">
        <div className="grantha-hero-copy">
          <Link to="/platforms" className="detail-back" data-testid="grantha-back">
            <ChevronLeft size={14} /> All platforms
          </Link>
          <div className="grantha-crumb mono">Platforms / Enterprise Digital / DOCUWEAVE</div>
          <div className="eyebrow" style={{ marginTop: 10 }}>Engineering Data &amp; Document Management</div>
          <h1 className="grantha-h1" data-testid="grantha-h1">
            <span className="grantha-indo gr-logo-lockup" data-testid="grantha-logo">
              <span className="rc-mark-wrap"><GranthaIcon size={88} /></span>
              <span className="rc-wordmark">
                Docu<span className="rc-c">w</span>eave
              </span>
            </span>
            <span className="grantha-h1-en">The canonical source of truth for <em>every engineering artifact</em>.</span>
          </h1>
          <p className="grantha-sub" data-testid="grantha-sub">
            From P&amp;ID Rev-0 to As-Built Rev-F, from transmittal T-2847 to signature #91 — Docuweave captures the full engineering lifecycle of a capital project with cognitive rigor. Built for owner-operators, EPCs, and PMCs who refuse to lose a single document across a decade-long megaproject.
          </p>
          <div className="grantha-ctas">
            <a href="https://docuweave.capexara.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="grantha-cta-access">Access Docuweave <span className="arrow">→</span></a>
            <Link to="/contact?p=docuweave" className="btn btn-ghost" data-testid="grantha-cta-demo">Request demo <span className="arrow">→</span></Link>
            <a href="#architecture" className="btn btn-ghost" data-testid="grantha-cta-arch">See architecture</a>
          </div>
          <div className="grantha-trust mono">
            Deployed on Azure · Integrates with Autodesk-class CAD/BIM BIM stack, document servers, 3D plant-design suites, plant-engineering platforms, engineering 3D suites
          </div>
        </div>
        <div className="grantha-hero-stage" data-testid="grantha-hero-stage">
          <GranthaDocumentFabric />
        </div>
      </div>
    </section>
  );
}

function DocumentVault() {
  return (
    <svg viewBox="0 0 560 560" className="grantha-vault" aria-label="Document vault visualization">
      <defs>
        <linearGradient id="vaultGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1E4E8C" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#2E70AD" stopOpacity="0.08" />
        </linearGradient>
        <linearGradient id="sheetGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#F3F4F6" />
        </linearGradient>
      </defs>
      {/* Outer halo */}
      <circle cx="280" cy="280" r="240" fill="#1E4E8C" fillOpacity="0.04" />
      <circle cx="280" cy="280" r="200" fill="none" stroke="#1E4E8C" strokeOpacity="0.15" strokeDasharray="3 8" style={{ transformOrigin: "280px 280px", animation: "orbitSpin 50s linear infinite" }} />

      {/* Isometric vault cube */}
      <g style={{ transformOrigin: "280px 280px", animation: "vaultFloat 6s ease-in-out infinite" }}>
        <polygon points="280,120 420,200 420,360 280,440 140,360 140,200" fill="url(#vaultGrad)" stroke="#1E4E8C" strokeWidth="1.5" strokeOpacity="0.8" />
        <polygon points="280,120 420,200 280,280 140,200" fill="#1E4E8C" fillOpacity="0.15" stroke="#1E4E8C" strokeWidth="1" strokeOpacity="0.5" />
        <polygon points="280,280 420,200 420,360 280,440" fill="#2E70AD" fillOpacity="0.08" stroke="#2E70AD" strokeWidth="1" strokeOpacity="0.4" />

        {/* Stacked drawing sheets */}
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i} style={{ animation: `vaultSheet 4s ease-in-out ${i * 0.4}s infinite` }}>
            <rect x={225 - i * 4} y={230 + i * 14} width="110" height="80" fill="url(#sheetGrad)" stroke="#1E4E8C" strokeOpacity="0.6" strokeWidth="0.8" transform={`skewY(-5)`} />
            <line x1={225 - i * 4} y1={244 + i * 14} x2={335 - i * 4} y2={244 + i * 14} stroke="#1E4E8C" strokeOpacity="0.4" strokeWidth="0.6" transform="skewY(-5)" />
            <line x1={225 - i * 4} y1={258 + i * 14} x2={335 - i * 4} y2={258 + i * 14} stroke="#1E4E8C" strokeOpacity="0.3" strokeWidth="0.6" transform="skewY(-5)" />
            <circle cx={310 - i * 4} cy={264 + i * 14} r="5" fill="#F26B1F" fillOpacity="0.4" transform="skewY(-5)" />
          </g>
        ))}
      </g>

      {/* Orbiting module nodes (6) */}
      {[
        { x: 440, y: 120, label: "DOC" },
        { x: 500, y: 280, label: "TAG" },
        { x: 440, y: 440, label: "TRN" },
        { x: 120, y: 440, label: "WF" },
        { x: 60, y: 280, label: "QTO" },
        { x: 120, y: 120, label: "DCI" },
      ].map((n, i) => (
        <g key={i} style={{ animation: `vaultNode 4s ease-in-out ${i * 0.2}s infinite` }}>
          <line x1="280" y1="280" x2={n.x} y2={n.y} stroke="#2E70AD" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="2 4" />
          <circle cx={n.x} cy={n.y} r="18" fill="var(--bg-elev)" stroke="#1E4E8C" strokeWidth="1.5" />
          <text x={n.x} y={n.y + 3} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9" fontWeight="700" fill="#1E4E8C">{n.label}</text>
          <circle r="2.5" fill="#F26B1F" style={{ offsetPath: `path('M 280 280 L ${n.x} ${n.y}')`, animation: `pipeFlow ${3 + i * 0.3}s linear infinite`, animationDelay: `${i * 0.4}s` }} />
        </g>
      ))}

      {/* Revision labels */}
      <text x="280" y="100" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#F26B1F" letterSpacing="3" fontWeight="700">REV-C · IFC · 2.3M DOCS</text>
    </svg>
  );
}

/* ───── S2 Problem ─────────────────────────────────── */
function Section2Problem() {
  const stats = [
    { n: 2.3, suffix: "M", label: "documents in a typical ₹5k Cr refinery project" },
    { n: 412, prefix: "₹", suffix: " Cr", label: "average cost of document-related rework on an EPC" },
    { n: 38, suffix: "%", label: "of RFI delays traced to wrong-revision drawings" },
    { n: 11000, suffix: "+", label: "tags in a mid-sized refinery — each linked to multiple documents" },
  ];
  return (
    <section className="grantha-problem" data-testid="grantha-problem">
      <div className="container">
        <div className="eyebrow" style={{ color: "var(--ember)" }}>The ₹400 Cr document problem</div>
        <h2 className="grantha-problem-h">
          <em>On the average megaproject, 18% of engineering hours are wasted searching for the right revision of the right document.</em>
        </h2>
        <div className="grantha-problem-grid">
          {stats.map((s, i) => (
            <div className="grantha-problem-tile" key={i} data-testid={`grantha-stat-${i}`}>
              <div className="grantha-problem-num">
                <CountUp end={s.n} prefix={s.prefix || ""} suffix={s.suffix || ""} decimals={s.n % 1 !== 0 ? 1 : 0} />
              </div>
              <div className="grantha-problem-label">{s.label}</div>
            </div>
          ))}
        </div>
        <p className="grantha-problem-quote">
          Documents don&rsquo;t just record engineering. <em>They are engineering.</em>
        </p>
      </div>
    </section>
  );
}

/* ───── S3 Architecture ─────────────────────────────────── */
function Section3Architecture() {
  const [activeLayer, setActiveLayer] = useState(2);
  const modules = MODULES.filter((m) => !m.dedupe);
  return (
    <section className="grantha-arch" id="architecture" data-testid="grantha-arch">
      <div className="container">
        <div className="eyebrow">Platform architecture</div>
        <h2 className="grantha-arch-h">
          Ten modules. <em>One document backbone.</em>
        </h2>
        <p className="grantha-arch-sub">
          Hover or click any layer to zoom in. Docuweave&rsquo;s eleven modules organise into four logical layers — from definition and structure, through capture, through lifecycle control, out to execution.
        </p>

        <div className="grantha-layers" data-testid="grantha-layers">
          {LAYERS.map((L) => (
            <button
              key={L.n}
              type="button"
              className={"grantha-layer " + (activeLayer === L.n ? "is-active" : "")}
              onMouseEnter={() => setActiveLayer(L.n)}
              onClick={() => setActiveLayer(L.n)}
              data-testid={`grantha-layer-${L.n}`}
            >
              <span className="grantha-layer-n mono">LAYER · 0{L.n}</span>
              <span className="grantha-layer-name">{L.name}</span>
              <span className="grantha-layer-count mono">{modules.filter((m) => m.layer === L.n).length}</span>
            </button>
          ))}
        </div>

        <div className="grantha-modules" data-testid="grantha-modules">
          {modules.filter((m) => m.layer === activeLayer).map((m) => (
            <Link key={m.slug} to={`/platforms/docuweave/modules/${m.slug}`} className="grantha-module" data-testid={`grantha-module-${m.slug}`}>
              <div className="grantha-module-icon"><m.Icon size={22} /></div>
              <div className="grantha-module-body">
                <div className="grantha-module-name">{m.name}</div>
                <div className="grantha-module-desc">{m.body}</div>
              </div>
              <div className="grantha-module-cta">View module <span className="arrow">→</span></div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── S4 Viewer ─────────────────────────────────── */
function Section4Viewer() {
  const features = [
    { n: "01", t: "Multi-format native rendering", b: "PDF, DWG, DGN, IFC, RVT, STEP, XLSX, DOCX — all in one viewer. No plugins." },
    { n: "02", t: "Tag-aware highlighting", b: "Hover any tag in a P&ID; Docuweave highlights every other drawing where that tag appears and lists linked documents in the sidebar." },
    { n: "03", t: "Revision-cloud auto-generation", b: "Markup a change; Docuweave draws the revision cloud, increments the rev letter, and updates the title block." },
    { n: "04", t: "Side-by-side revision compare", b: "Diff Rev-A vs Rev-B visually, with additions in orange and deletions in Steel Blue." },
    { n: "05", t: "document servers inline editing", b: "Edit Word, Excel, PowerPoint in-browser with full track-changes. No download / upload round-trips." },
    { n: "06", t: "3D model federation", b: "Load IFC / RVT / DWG models in the same viewer via APS/Forge. Click a tag, the 3D camera flies to the physical asset." },
  ];
  return (
    <section className="grantha-viewer" data-testid="grantha-viewer">
      <div className="container grantha-viewer-grid">
        <div className="grantha-viewer-visual">
          <div className="grantha-viewer-frame">
            <div className="grantha-viewer-bar">
              <span className="mono">P-6401-PID-004 · Rev-B · IFC</span>
              <span className="mono" style={{ color: "var(--ember)" }}>● MARKUP ACTIVE</span>
            </div>
            <svg viewBox="0 0 720 460" className="grantha-viewer-svg" preserveAspectRatio="xMidYMid meet">
              <defs>
                {/* ▪ canvas */}
                <linearGradient id="gr-canvas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0B1220" />
                  <stop offset="100%" stopColor="#070A12" />
                </linearGradient>
                {/* ▪ CAD grid */}
                <pattern id="gr-grid-minor" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M20 0H0V20" fill="none" stroke="#1E4E8C" strokeOpacity="0.08" strokeWidth="0.5" />
                </pattern>
                <pattern id="gr-grid-major" width="80" height="80" patternUnits="userSpaceOnUse">
                  <path d="M80 0H0V80" fill="none" stroke="#1E4E8C" strokeOpacity="0.18" strokeWidth="0.7" />
                </pattern>
                {/* ▪ ambient glow behind highlighted tag */}
                <radialGradient id="gr-tag-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#F26B1F" stopOpacity="0.35" />
                  <stop offset="60%" stopColor="#F26B1F" stopOpacity="0.08" />
                  <stop offset="100%" stopColor="#F26B1F" stopOpacity="0" />
                </radialGradient>
                {/* ▪ arrow */}
                <marker id="gr-arrow-steel" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                  <path d="M0,0 L10,5 L0,10 z" fill="#5790C9" />
                </marker>
                <marker id="gr-arrow-ember" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0,0 L10,5 L0,10 z" fill="#F26B1F" />
                </marker>
              </defs>

              {/* ═══ CANVAS + GRID ═══ */}
              <rect x="0" y="0" width="720" height="460" fill="url(#gr-canvas)" />
              <rect x="0" y="0" width="720" height="460" fill="url(#gr-grid-minor)" />
              <rect x="0" y="0" width="720" height="460" fill="url(#gr-grid-major)" />
              {/* ruler ticks — left edge */}
              {Array.from({ length: 9 }, (_, i) => i).map((i) => (
                <line key={`ry${i}`} x1="0" y1={50 + i * 45} x2={i % 2 ? 8 : 14} y2={50 + i * 45} stroke="#2E70AD" strokeOpacity="0.45" strokeWidth="0.8" />
              ))}
              {/* ruler ticks — top edge */}
              {Array.from({ length: 14 }, (_, i) => i).map((i) => (
                <line key={`rx${i}`} x1={40 + i * 48} y1="0" x2={40 + i * 48} y2={i % 2 ? 8 : 14} stroke="#2E70AD" strokeOpacity="0.45" strokeWidth="0.8" />
              ))}

              {/* ═══ AMBIENT GLOW behind FE-4421 ═══ */}
              <circle cx="430" cy="240" r="90" fill="url(#gr-tag-glow)" style={{ animation: "glowPulse 3.2s ease-in-out infinite" }} />

              {/* ═════════════════════════════════════════════════
                  PROCESS LINES  (laid down before equipment)
                  ═════════════════════════════════════════════════ */}
              <g stroke="#5790C9" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round">
                {/* MAIN FLOW: inlet (40,140) → V-101 → E-201 → FE-4421 → FV-442 → outlet */}
                <path d="M40 140 H140" />                                   {/* feed in → V-101 */}
                <path d="M220 140 H330" />                                  {/* V-101 → E-201 shell in */}
                <path d="M430 140 Q470 140 470 200 V240 H560" />            {/* E-201 → FE → FV */}
                <path d="M620 240 H700" />                                  {/* FV → product out */}
                {/* UTILITY: cooling water loop */}
                <path d="M380 60 V100" />                                   {/* CW-IN top */}
                <path d="M380 180 V220 H560 V300" />                        {/* shell-side drain to sump */}
                {/* RECYCLE (dashed returning to V-101) */}
                <path d="M680 240 V360 H180 V220" strokeDasharray="6 4" strokeOpacity="0.55" />
              </g>

              {/* ANIMATED FLOW DASHES on main line */}
              <path d="M40 140 H140" stroke="#8BB7E0" strokeWidth="1.2" fill="none"
                    strokeDasharray="4 5" style={{ animation: "flowDash 3.5s linear infinite" }} />
              <path d="M220 140 H330" stroke="#8BB7E0" strokeWidth="1.2" fill="none"
                    strokeDasharray="4 5" style={{ animation: "flowDash 3.5s linear infinite" }} />
              {/* EMBER dashes — the NEW Rev-B route */}
              <path d="M430 140 Q470 140 470 200 V240 H560" stroke="#F26B1F" strokeWidth="1.4" fill="none"
                    strokeDasharray="5 5" strokeOpacity="0.85"
                    style={{ animation: "flowDash 2.4s linear infinite" }} />

              {/* ═════════════════════════════════════════════════
                  EQUIPMENT
                  ═════════════════════════════════════════════════ */}

              {/* V-101 VERTICAL VESSEL (x=180, y=100..190) */}
              <g>
                <rect x="158" y="105" width="44" height="75" fill="#0F1628" stroke="#5790C9" strokeWidth="1.8" />
                <path d="M158 105 Q180 88 202 105" fill="#0F1628" stroke="#5790C9" strokeWidth="1.8" />
                <path d="M158 180 Q180 197 202 180" fill="#0F1628" stroke="#5790C9" strokeWidth="1.8" />
                {/* internal liquid level */}
                <rect x="160" y="140" width="40" height="38" fill="#2E70AD" fillOpacity="0.2" />
                <line x1="160" y1="140" x2="200" y2="140" stroke="#6AC8FF" strokeWidth="0.8" strokeOpacity="0.6" />
                {/* nozzle stubs */}
                <line x1="140" y1="140" x2="158" y2="140" stroke="#5790C9" strokeWidth="1.6" />
                <line x1="202" y1="140" x2="220" y2="140" stroke="#5790C9" strokeWidth="1.6" />
                {/* tag */}
                <text x="180" y="212" textAnchor="middle" fontSize="9" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700" letterSpacing="1">V-101</text>
              </g>

              {/* E-201 SHELL-AND-TUBE HEAT EXCHANGER (x=330..430, y=100..180) */}
              <g>
                <rect x="330" y="115" width="100" height="50" fill="#0F1628" stroke="#5790C9" strokeWidth="1.8" rx="2" />
                {/* tube bundle */}
                {[125, 133, 141, 149, 157].map((y) => (
                  <line key={y} x1="335" y1={y} x2="425" y2={y} stroke="#5790C9" strokeOpacity="0.5" strokeWidth="0.8" />
                ))}
                {/* end caps */}
                <rect x="326" y="110" width="8" height="60" fill="#0F1628" stroke="#5790C9" strokeWidth="1.5" />
                <rect x="426" y="110" width="8" height="60" fill="#0F1628" stroke="#5790C9" strokeWidth="1.5" />
                <text x="380" y="104" textAnchor="middle" fontSize="9" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700" letterSpacing="1">E-201</text>
              </g>

              {/* P-101 CENTRIFUGAL PUMP (x=90, y=360) — ISO symbol */}
              <g transform="translate(90, 360)">
                <circle r="14" fill="#0F1628" stroke="#5790C9" strokeWidth="1.8" />
                <path d="M-10 -8 L10 0 L-10 8 Z" fill="#2E70AD" fillOpacity="0.45" stroke="#5790C9" strokeWidth="1.2" />
                <line x1="-14" y1="0" x2="-22" y2="0" stroke="#5790C9" strokeWidth="1.4" />
                <line x1="14" y1="0" x2="22" y2="0" stroke="#5790C9" strokeWidth="1.4" />
                <text y="32" textAnchor="middle" fontSize="9" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700" letterSpacing="1">P-101</text>
              </g>
              {/* P-101 → V-101 riser */}
              <path d="M90 346 V220 H158" stroke="#5790C9" strokeWidth="1.6" fill="none" />

              {/* FE-4421 ORIFICE PLATE (highlighted, x=430, y=240) */}
              <g>
                {/* orifice plate */}
                <line x1="428" y1="228" x2="432" y2="228" stroke="#F26B1F" strokeWidth="2.2" />
                <line x1="428" y1="252" x2="432" y2="252" stroke="#F26B1F" strokeWidth="2.2" />
                <line x1="430" y1="228" x2="430" y2="236" stroke="#F26B1F" strokeWidth="2.2" />
                <line x1="430" y1="244" x2="430" y2="252" stroke="#F26B1F" strokeWidth="2.2" />
                {/* pulse ring */}
                <circle cx="430" cy="240" r="22" fill="none" stroke="#F26B1F" strokeWidth="1.4" strokeDasharray="3 4"
                        style={{ transformOrigin: "430px 240px", animation: "orbitSpin 7s linear infinite" }} />
              </g>

              {/* FV-442 CONTROL VALVE (x=590, y=240) */}
              <g transform="translate(590, 240)">
                {/* actuator diamond above */}
                <path d="M-10 -14 L10 -14 L14 -20 L-14 -20 Z" fill="#0F1628" stroke="#5790C9" strokeWidth="1.3" />
                <line x1="0" y1="-14" x2="0" y2="-8" stroke="#5790C9" strokeWidth="1" strokeDasharray="2 2" />
                {/* globe valve bowtie */}
                <path d="M-14 -8 L0 0 L-14 8 Z" fill="#0F1628" stroke="#5790C9" strokeWidth="1.6" />
                <path d="M14 -8 L0 0 L14 8 Z" fill="#0F1628" stroke="#5790C9" strokeWidth="1.6" />
                <circle r="2.4" fill="#F26B1F" />
                <text y="28" textAnchor="middle" fontSize="9" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700" letterSpacing="1">FV-442</text>
              </g>

              {/* ═════════════════════════════════════════════════
                  INSTRUMENT BUBBLES (ISA-5.1)
                  ═════════════════════════════════════════════════ */}
              {/* FE-4421 field bubble */}
              <g>
                <line x1="430" y1="228" x2="430" y2="200" stroke="#5790C9" strokeWidth="0.9" />
                <circle cx="430" cy="188" r="13" fill="#0F1628" stroke="#F26B1F" strokeWidth="1.6" />
                <line x1="417" y1="188" x2="443" y2="188" stroke="#F26B1F" strokeWidth="0.8" strokeOpacity="0.45" />
                <text x="430" y="186" textAnchor="middle" fontSize="7.5" fill="#F26B1F" fontFamily="JetBrains Mono, monospace" fontWeight="700">FE</text>
                <text x="430" y="195" textAnchor="middle" fontSize="7.5" fill="#F26B1F" fontFamily="JetBrains Mono, monospace" fontWeight="700">4421</text>
              </g>
              {/* FIC-442 DCS bubble (double-line) */}
              <g>
                <line x1="590" y1="220" x2="590" y2="172" stroke="#5790C9" strokeWidth="0.9" />
                <circle cx="590" cy="158" r="14" fill="#0F1628" stroke="#5790C9" strokeWidth="1.4" />
                <circle cx="590" cy="158" r="11" fill="none" stroke="#5790C9" strokeWidth="1.1" />
                <line x1="578" y1="158" x2="602" y2="158" stroke="#5790C9" strokeWidth="0.8" />
                <text x="590" y="156" textAnchor="middle" fontSize="7.5" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700">FIC</text>
                <text x="590" y="165" textAnchor="middle" fontSize="7.5" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700">442</text>
              </g>
              {/* TI-401 on vessel outlet */}
              <g>
                <line x1="210" y1="140" x2="210" y2="90" stroke="#5790C9" strokeWidth="0.9" strokeDasharray="2 2" />
                <circle cx="210" cy="78" r="12" fill="#0F1628" stroke="#5790C9" strokeWidth="1.3" />
                <text x="210" y="76" textAnchor="middle" fontSize="7" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700">TI</text>
                <text x="210" y="84" textAnchor="middle" fontSize="7" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700">401</text>
              </g>
              {/* LT-501 on vessel side */}
              <g>
                <line x1="202" y1="158" x2="258" y2="158" stroke="#5790C9" strokeWidth="0.9" strokeDasharray="2 2" />
                <circle cx="272" cy="158" r="12" fill="#0F1628" stroke="#5790C9" strokeWidth="1.3" />
                <text x="272" y="156" textAnchor="middle" fontSize="7" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700">LT</text>
                <text x="272" y="164" textAnchor="middle" fontSize="7" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700">501</text>
              </g>
              {/* PT-201 on E-201 outlet */}
              <g>
                <line x1="430" y1="140" x2="460" y2="108" stroke="#5790C9" strokeWidth="0.9" />
                <circle cx="468" cy="98" r="11" fill="#0F1628" stroke="#5790C9" strokeWidth="1.3" />
                <text x="468" y="96" textAnchor="middle" fontSize="6.5" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700">PT</text>
                <text x="468" y="104" textAnchor="middle" fontSize="6.5" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace" fontWeight="700">201</text>
              </g>

              {/* Flow-arrow markers on main line */}
              <path d="M120 140 H125" stroke="#5790C9" strokeWidth="1.4" markerEnd="url(#gr-arrow-steel)" />
              <path d="M680 240 H685" stroke="#5790C9" strokeWidth="1.4" markerEnd="url(#gr-arrow-steel)" />

              {/* ═════════════════════════════════════════════════
                  REV-B MARKUP — sinuous cloud + ribbon + callout
                  ═════════════════════════════════════════════════ */}
              <g>
                {/* sinuous revision cloud around FE/FV area */}
                <path d="M400 200
                         Q395 188 406 186 Q410 174 424 178 Q432 168 446 176
                         Q462 170 472 184 Q488 184 492 200 Q508 208 500 222
                         Q512 234 500 248 Q506 262 488 266 Q480 280 462 274
                         Q450 284 434 276 Q420 286 408 274 Q392 276 390 260
                         Q378 252 388 236 Q378 224 392 214 Q390 204 400 200 Z"
                      fill="none" stroke="#F26B1F" strokeWidth="1.6"
                      style={{ animation: "shimmerLine 2.4s ease-in-out infinite" }} />
                {/* REV-B ribbon */}
                <g transform="translate(498, 170)">
                  <path d="M0 0 H44 L50 7 L44 14 H0 Z" fill="#F26B1F" />
                  <text x="22" y="10.5" textAnchor="middle" fontSize="9" fill="#0A0E1A" fontFamily="JetBrains Mono, monospace" fontWeight="800" letterSpacing="1.4">REV-B</text>
                </g>
              </g>

              {/* ═════════════════════════════════════════════════
                  SMART TAG CALLOUT — "FE-4421 linked to 12 drawings"
                  ═════════════════════════════════════════════════ */}
              <g transform="translate(32, 310)" style={{ animation: "rise .9s cubic-bezier(.2,.8,.2,1) .3s both" }}>
                <rect x="0" y="0" width="172" height="62" rx="8" fill="#0F1628" stroke="#2E70AD" strokeOpacity="0.55" strokeWidth="1" />
                <rect x="0" y="0" width="4" height="62" rx="2" fill="#F26B1F" />
                <text x="14" y="16" fontSize="8" fill="#F26B1F" fontFamily="JetBrains Mono, monospace" fontWeight="700" letterSpacing="1.6">LINKED DRAWINGS</text>
                <text x="14" y="32" fontSize="11" fill="#F3F4F6" fontFamily="JetBrains Mono, monospace" fontWeight="700">FE-4421</text>
                <text x="14" y="46" fontSize="9" fill="#C2D5EE" fontFamily="JetBrains Mono, monospace">12 drawings · 4 specs · 2 models</text>
                {/* mini-thumbnail strip */}
                {[0,1,2,3,4,5].map((i) => (
                  <rect key={i} x={14 + i * 22} y={50} width="18" height="8" rx="1.2"
                        fill="none" stroke="#2E70AD" strokeOpacity={i < 4 ? "0.7" : "0.35"} strokeWidth="0.7" />
                ))}
                {/* connector line to orifice */}
                <path d="M172 18 Q280 0 398 226" stroke="#F26B1F" strokeWidth="0.8" strokeDasharray="2 3" strokeOpacity="0.55" fill="none" />
              </g>

              {/* ═════════════════════════════════════════════════
                  BOTTOM ANNOTATIONS — scale bar + coords + meta
                  ═════════════════════════════════════════════════ */}
              <g transform="translate(24, 430)">
                {/* scale bar */}
                <line x1="0" y1="0" x2="120" y2="0" stroke="#5790C9" strokeWidth="1" />
                {[0, 30, 60, 90, 120].map((x) => (
                  <line key={x} x1={x} y1="-4" x2={x} y2="0" stroke="#5790C9" strokeWidth="1" />
                ))}
                <text x="0" y="14" fontSize="7.5" fill="#5790C9" fontFamily="JetBrains Mono, monospace" letterSpacing="1">0</text>
                <text x="60" y="14" textAnchor="middle" fontSize="7.5" fill="#5790C9" fontFamily="JetBrains Mono, monospace" letterSpacing="1">5m</text>
                <text x="120" y="14" textAnchor="end" fontSize="7.5" fill="#5790C9" fontFamily="JetBrains Mono, monospace" letterSpacing="1">10m</text>
              </g>
              <text x="240" y="444" fontSize="8" fill="#5790C9" fontFamily="JetBrains Mono, monospace" letterSpacing="1.4">X 4,238 · Y 1,104</text>
              <text x="420" y="444" fontSize="8" fill="#5790C9" fontFamily="JetBrains Mono, monospace" letterSpacing="1.4">ISA-5.1 · IFC · 24 TAGS</text>
              <text x="696" y="444" textAnchor="end" fontSize="8" fill="#F26B1F" fontFamily="JetBrains Mono, monospace" letterSpacing="1.4">● AI-NATIVE</text>
            </svg>
            <div className="grantha-viewer-meta mono">
              <div>Tag · FE-4421 · linked to 12 drawings</div>
              <div style={{ color: "var(--ember)" }}>Markup · FI-4421 line route changed Rev-A → Rev-B</div>
            </div>
          </div>
        </div>
        <div className="grantha-viewer-copy">
          <div className="eyebrow">The document viewer</div>
          <h2 className="grantha-viewer-h"><em>The viewer</em> that understands what it&rsquo;s showing.</h2>
          <div className="grantha-features">
            {features.map((f) => (
              <div className="grantha-feature" key={f.n}>
                <span className="grantha-feature-n mono">{f.n}</span>
                <div>
                  <div className="grantha-feature-t">{f.t}</div>
                  <div className="grantha-feature-b">{f.b}</div>
                </div>
              </div>
            ))}
          </div>
          <blockquote className="grantha-pull">
            Engineering lives in drawings. <em>Docuweave makes the drawings speak.</em>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

/* ───── S5 Workflow ─────────────────────────────────── */
function Section5Workflow() {
  const steps = [
    { role: "Originator", who: "Piping Lead", sla: "24h" },
    { role: "Interdisc. check", who: "Process · Instr · Safety", sla: "48h", parallel: true },
    { role: "Project Engineer", who: "PE · discipline sign-off", sla: "24h" },
    { role: "Client Rep", who: "IFC decision", sla: "72h" },
    { role: "Archive", who: "Released as IFC", sla: "— ", done: true },
  ];
  return (
    <section className="grantha-wf" data-testid="grantha-wf">
      <div className="container grantha-wf-grid">
        <div>
          <div className="eyebrow" style={{ color: "var(--steel-200)" }}>The orchestration spine</div>
          <h2 className="grantha-wf-h">Enterprise-grade workflow engine with <em>granular step authorization.</em></h2>
          <p className="grantha-wf-body">
            Docuweave&rsquo;s Workflow Execution Engine is built on a battle-tested step-level enforcement pattern — every step of every approval chain checks the user&rsquo;s role, discipline, PBS scope, delegation state, and SLA window before allowing action.
          </p>
          <ul className="grantha-wf-list">
            <li>N-level parallel and serial approval routing</li>
            <li>Conditional branching on document type, cost impact, discipline, or PBS node</li>
            <li>Delegation chains with full audit trail</li>
            <li>SLA timers with auto-escalation to supervisors</li>
            <li>Digital signatures — DSC-compatible, Aadhaar eSign optional</li>
            <li>Full workflow replay for audit (every click, every signature, preserved)</li>
          </ul>
        </div>
        <div className="grantha-wf-diagram" data-testid="grantha-wf-diagram">
          <div className="grantha-wf-track">
            {steps.map((s, i) => (
              <div key={i} className={"grantha-wf-step " + (s.done ? "is-done " : "") + (s.parallel ? "is-parallel " : "")}>
                <div className="grantha-wf-bullet" />
                <div className="grantha-wf-meta">
                  <div className="mono" style={{ color: "var(--text-3)", fontSize: 10, letterSpacing: ".18em" }}>{s.role}</div>
                  <div style={{ color: "#F3F4F6", fontSize: 13, fontWeight: 500 }}>{s.who}</div>
                  <div className="mono" style={{ color: "#F26B1F", fontSize: 10 }}>SLA · {s.sla}</div>
                </div>
                {i < steps.length - 1 && <div className="grantha-wf-connector" />}
              </div>
            ))}
          </div>
          <div className="grantha-wf-packet" style={{ animation: "wfPacket 10s ease-in-out infinite" }}>
            <FileText size={14} />
            <span className="mono">P-6401-PID-004</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── S7 Industries ─────────────────────────────────── */
function Section7Industries() {
  const chips = [
    "Oil & Gas — Upstream · Pipeline · LNG · Downstream · Hydrogen",
    "Power — Coal · Gas · CCGT · Nuclear · Solar · Wind",
    "Power T&D · BESS · Water & Desalination",
    "Chemicals — Basic · Specialty",
    "Metals, Mining & Minerals",
    "Infrastructure — Data Centers · Metros · Bridges",
    "Marine — Ports · Shipbuilding",
    "Manufacturing — Life Sciences · P&P · Discrete",
  ];
  return (
    <section className="grantha-inds" data-testid="grantha-inds">
      <div className="container">
        <div className="eyebrow">Industries served</div>
        <h2 className="grantha-inds-h">Deployed across <em>every heavy-industry vertical</em> we serve.</h2>
        <div className="grantha-chips">
          {chips.map((c) => (
            <Link to="/industries" className="grantha-chip" key={c} data-testid="grantha-ind-chip">{c}</Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── S8 Outcomes ─────────────────────────────────── */
function Section8Outcomes() {
  const cards = [
    { big: 42, suffix: "%", head: "reduction in document search time", body: "From 18% wasted engineering hours → 10%." },
    { big: 87, prefix: "₹", suffix: " Cr", head: "average annual savings on a ₹5,000 Cr EPC project", body: "Rework avoided + audit-penalty reduction + faster closeout." },
    { big: 3.2, suffix: "× faster", head: "document retrieval vs document libraries / Documentum", body: "Tag-linked search + IFC metadata indexing.", decimals: 1 },
  ];
  return (
    <section className="grantha-out" data-testid="grantha-out">
      <div className="container">
        <div className="eyebrow">Measured impact</div>
        <h2 className="grantha-out-h">The business case, <em>validated on real projects.</em></h2>
        <div className="grantha-out-grid">
          {cards.map((c, i) => (
            <div className="grantha-out-card" key={i} data-testid={`grantha-out-${i}`}>
              <div className="grantha-out-num">
                <CountUp end={c.big} prefix={c.prefix || ""} suffix={c.suffix || ""} decimals={c.decimals || 0} />
              </div>
              <div className="grantha-out-head">{c.head}</div>
              <div className="grantha-out-body">{c.body}</div>
            </div>
          ))}
        </div>
        <Link to="/resources/wp-02" className="grantha-out-cta">Request the full ROI whitepaper <span className="arrow">→</span></Link>
      </div>
    </section>
  );
}

/* ───── S9 FAQ ─────────────────────────────────── */
function Section9Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="grantha-faq" data-testid="grantha-faq">
      <div className="container">
        <div className="eyebrow">Frequently asked</div>
        <h2 className="grantha-faq-h">Everything a <em>Document Control Manager</em> wants to know.</h2>
        <div className="grantha-faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={"grantha-faq-item " + (isOpen ? "is-open" : "")} data-testid={`grantha-faq-${i}`}>
                <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} className="grantha-faq-q" aria-expanded={isOpen}>
                  <span>{f.q}</span>
                  {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                </button>
                {isOpen && <div className="grantha-faq-a">{f.a}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───── S10 CTA ─────────────────────────────────── */
function Section10Cta() {
  return (
    <section className="grantha-cta" data-testid="grantha-cta-section">
      <div className="container grantha-cta-inner">
        <div className="eyebrow" style={{ color: "#F26B1F" }}>See Docuweave in action</div>
        <h2 className="grantha-cta-h">See your own documents in Docuweave — <em>in 30 minutes.</em></h2>
        <p className="grantha-cta-sub">
          Book a live demo. Bring one of your current project&rsquo;s drawings. We&rsquo;ll load it, tag it, run it through a workflow, and show you the ROI math specific to your organization.
        </p>
        <div className="grantha-cta-row">
          <Link to="/contact?p=docuweave" className="btn btn-primary grantha-cta-btn" data-testid="grantha-cta-demo-btn">Book a 30-min demo <span className="arrow">→</span></Link>
          <a href="#" className="btn btn-ghost grantha-cta-ghost">Download datasheet (PDF)</a>
        </div>
      </div>
    </section>
  );
}

/* Module stub page (used by route) */
export function PlatformGranthaModule() {
  const navigate = useNavigate();
  const slug = window.location.pathname.split("/").pop();
  const m = MODULES.find((x) => x.slug === slug);
  return (
    <section className="detail-body" style={{ paddingTop: 140 }} data-testid="grantha-module-detail">
      <div className="container">
        <Link to="/platforms/docuweave" className="detail-back">
          <ChevronLeft size={14} /> Back to Docuweave
        </Link>
        <div className="eyebrow" style={{ marginTop: 16 }}>Docuweave module</div>
        <h1 className="detail-hero-title" data-testid="grantha-module-h1">{m ? m.name : slug}</h1>
        {m ? (
          <p style={{ fontSize: 18, color: "var(--text-2)", maxWidth: 760, lineHeight: 1.6, marginTop: 16 }}>{m.body}</p>
        ) : (
          <p style={{ color: "var(--text-2)" }}>Module detail coming soon.</p>
        )}
        <div style={{ marginTop: 40 }}>
          <Link to="/contact?p=docuweave" className="btn btn-primary">Book a walkthrough <span className="arrow">→</span></Link>
        </div>
      </div>
    </section>
  );
}
